import React, { useEffect, useState } from 'react';
import { Container, Table, Button, Modal, Form, Row, Col, Pagination, Spinner, Image, Nav, Card, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { ApiManager } from '../../core/api-manager';
import { ViewManager } from '../../core/view-manager';
import { AuthorizationManager } from '../../core/authorization-manager';
import { LanguageProvider } from '../../core/language-provider';
import FieldPreview from '../../components/Entity/FieldPreview';
import Field from '../../components/Entity/Field';
import avatars from '../../core/avatar';
import './style.css';

const ENTITY = 'user';

export default function Users() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const limit = 10;

    // Tab attiva: 'active' o 'disabled'
    const [activeTab, setActiveTab] = useState('active');

    // Ricerca
    const [searchQuery, setSearchQuery] = useState('');

    // Entity properties
    const [props, setProps] = useState(null);
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [editableColumns, setEditableColumns] = useState([]);

    // Modal edit
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [saving, setSaving] = useState(false);

    // Modal password
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [passwordUser, setPasswordUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [savingPassword, setSavingPassword] = useState(false);

    // Modal delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingUser, setDeletingUser] = useState(null);
    const [deleting, setDeleting] = useState(false);

    // Modal disable user
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [disablingUser, setDisablingUser] = useState(null);
    const [disabling, setDisabling] = useState(false);

    // Modal enable user
    const [showEnableModal, setShowEnableModal] = useState(false);
    const [enablingUser, setEnablingUser] = useState(null);
    const [enabling, setEnabling] = useState(false);

    // Modal restore user
    const [showRestoreModal, setShowRestoreModal] = useState(false);
    const [restoringUser, setRestoringUser] = useState(null);
    const [restoring, setRestoring] = useState(false);


    // Permissions
    const canView = AuthorizationManager.checkEntityActionAuth(ENTITY, 'view') !== false;
    const canEdit = AuthorizationManager.checkEntityActionAuth(ENTITY, 'edit') !== false;
    const canCreate = AuthorizationManager.checkEntityActionAuth(ENTITY, 'create') !== false;
    const canDelete = AuthorizationManager.checkEntityActionAuth(ENTITY, 'delete') !== false;

    useEffect(() => {
        const entityProps = ViewManager.getEntityProperties(ENTITY);
        setProps(entityProps);

        // Filtra le colonne visibili escludendo deepExternalEntity
        const allVisibleColumns = ViewManager.getVisibleColumns(ENTITY) || [];
        const filteredColumns = allVisibleColumns.filter(col => {
            const colConfig = entityProps?.columns?.[col];
            return colConfig?.input_type !== 'deepExternalEntity';
        });
        setVisibleColumns(filteredColumns);

        setEditableColumns(ViewManager.getEditableColumns(ENTITY) || []);
    }, []);

    useEffect(() => {
        if (props) {
            loadUsers();
        }
    }, [page, props, activeTab, searchQuery]);

    // Reset pagina quando cambia tab
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(1);
    };

    // Gestione ricerca
    const handleSearch = (value) => {
        setSearchQuery(value);
        setPage(1);
    };

    const loadUsers = async () => {
        setLoading(true);
        try {
            let response;

            if (activeTab === 'deleted') {
                // Per utenti eliminati usa API custom (la search standard esclude deleted=1)
                response = await ApiManager.sendAuthenticatedRequest('/users/getDeleted', {
                    page: page,
                    limit: limit
                });
            } else {
                // Filtra per utenti attivi o disabilitati in base alla tab
                const disabledValue = activeTab === 'disabled' ? 1 : 0;
                const constraints = [
                    { fieldName: 'disabled', type: 'value', value: disabledValue }
                ];

                // Campi in cui cercare
                const searchFields = ['name', 'surname', 'username', 'email'];

                response = await DataManager.searchItems(
                    ENTITY,
                    page,
                    limit,
                    { field: 'id', order: 'DESC' },
                    searchQuery,
                    searchFields,
                    constraints
                );
            }

            if (response.success === 1) {
                setUsers(response.body || []);
                setTotalItems(response.total || 0);
                setTotalPages(Math.ceil((response.total || 0) / limit));
            }
        } catch (error) {
            console.error('Error loading users:', error);
            toast.error('Errore nel caricamento utenti');
        } finally {
            setLoading(false);
        }
    };

    const getAvatarImage = (user) => {
        if (user.avatar === null || user.avatar === undefined) return null;

        // Avatar può essere un numero diretto o un oggetto { value: X }
        const avatarIndex = user.avatar?.value !== undefined ? user.avatar.value : user.avatar;

        if (avatarIndex !== null && avatarIndex !== undefined && avatars[avatarIndex]) {
            return avatars[avatarIndex];
        }
        return null;
    };

    // Controlla se un utente è disabilitato
    const isUserDisabled = (user) => {
        // Controlla il campo disabled (può essere valore diretto o oggetto con value)
        const disabled = user.disabled?.value !== undefined ? user.disabled.value : user.disabled;
        return disabled === 1 || disabled === '1' || disabled === true;
    };

    // Disabilita utente
    const handleDisableClick = (user) => {
        setDisablingUser(user);
        setShowDisableModal(true);
    };

    const handleConfirmDisable = async () => {
        if (!disablingUser) return;

        setDisabling(true);
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/users/disable', {
                id: disablingUser.id
            });

            if (response.success === 1) {
                toast.success('Utente disabilitato');
                setShowDisableModal(false);
                setDisablingUser(null);
                loadUsers();
            } else {
                toast.error(response.message || 'Errore nella disabilitazione');
            }
        } catch (error) {
            console.error('Error disabling user:', error);
            toast.error('Errore nella disabilitazione');
        } finally {
            setDisabling(false);
        }
    };

    // Riabilita utente
    const handleEnableClick = (user) => {
        setEnablingUser(user);
        setShowEnableModal(true);
    };

    const handleConfirmEnable = async () => {
        if (!enablingUser) return;

        setEnabling(true);
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/users/enable', {
                id: enablingUser.id
            });

            if (response.success === 1) {
                toast.success('Utente riabilitato');
                setShowEnableModal(false);
                setEnablingUser(null);
                loadUsers();
            } else {
                toast.error(response.message || 'Errore nella riabilitazione');
            }
        } catch (error) {
            console.error('Error enabling user:', error);
            toast.error('Errore nella riabilitazione');
        } finally {
            setEnabling(false);
        }
    };

    // Ripristina utente eliminato
    const handleRestoreClick = (user) => {
        setRestoringUser(user);
        setShowRestoreModal(true);
    };

    const handleConfirmRestore = async () => {
        if (!restoringUser) return;

        setRestoring(true);
        try {
            const response = await ApiManager.sendAuthenticatedRequest('/users/restore', {
                id: restoringUser.id
            });

            if (response.success === 1) {
                toast.success('Utente ripristinato');
                setShowRestoreModal(false);
                setRestoringUser(null);
                loadUsers();
            } else {
                toast.error(response.message || 'Errore nel ripristino');
            }
        } catch (error) {
            console.error('Error restoring user:', error);
            toast.error('Errore nel ripristino');
        } finally {
            setRestoring(false);
        }
    };

    // Callback per aggiornare un campo nell'editingUser
    const handleFieldChange = (fieldKey, value) => {
        setEditingUser(prev => ({ ...prev, [fieldKey]: value }));
    };

    // Estrae il valore di un campo dalla risposta API per l'editing
    // Mantiene il formato oggetto per option e externalEntity (richiesto da Field component)
    const extractFieldValue = (fieldData, fieldConfig) => {
        if (fieldData === null || fieldData === undefined) {
            if (fieldConfig.input_type === 'boolean') return 0;
            if (fieldConfig.input_type === 'option') return { value: '', label: '' };
            if (fieldConfig.input_type === 'externalEntity') return null;
            return '';
        }

        switch (fieldConfig.input_type) {
            case 'option':
                // Field component si aspetta { value: X, label: Y }
                if (typeof fieldData === 'object' && fieldData.value !== undefined) {
                    return fieldData;
                }
                return { value: fieldData, label: fieldData };
            case 'externalEntity':
                // Field component si aspetta l'oggetto completo o { id: X }
                return fieldData;
            case 'boolean':
                return parseInt(fieldData) || 0;
            default:
                return fieldData;
        }
    };

    // Inizializza un campo vuoto per nuovo record
    // Formato compatibile con Field component
    const initFieldValue = (fieldConfig) => {
        switch (fieldConfig.input_type) {
            case 'boolean':
                return fieldConfig.options?.default ?? 0;
            case 'option':
                const defaultVal = fieldConfig.options?.default ?? '';
                return { value: defaultVal, label: defaultVal };
            case 'externalEntity':
            case 'multipleExternalEntity':
                return null;
            default:
                return fieldConfig.options?.default ?? '';
        }
    };

    const handleEdit = async (user) => {
        try {
            const response = await DataManager.getItem(ENTITY, user.id);
            if (response.success === 1 && response.body && response.body[0]) {
                const fullUser = response.body[0];
                const userData = { id: fullUser.id };

                editableColumns.forEach(col => {
                    if (props.columns[col]) {
                        userData[col] = extractFieldValue(fullUser[col], props.columns[col]);
                    }
                });

                setEditingUser(userData);
                setShowEditModal(true);
            }
        } catch (error) {
            console.error('Error loading user:', error);
            toast.error('Errore nel caricamento utente');
        }
    };

    const handleAdd = () => {
        const userData = {};

        editableColumns.forEach(col => {
            if (props.columns[col]) {
                userData[col] = initFieldValue(props.columns[col]);
            }
        });

        setEditingUser(userData);
        setShowEditModal(true);
    };

    // Prepara il valore di un campo per il salvataggio (formato atteso da parseData)
    // I valori dal Field component sono già in formato oggetto
    const prepareFieldForSave = (value, fieldConfig) => {
        if (value === '' || value === null || value === undefined) {
            return null;
        }

        switch (fieldConfig.input_type) {
            case 'option':
                // Field salva già come { value: X, label: Y }
                if (typeof value === 'object' && value.value !== undefined) {
                    return value.value === '' ? null : value;
                }
                return value === '' ? null : { value: value };
            case 'externalEntity':
                // Field salva già come oggetto con id
                if (typeof value === 'object' && value.id !== undefined) {
                    return value;
                }
                return value ? { id: value } : null;
            default:
                return value;
        }
    };

    // Controlla se un campo è vuoto (gestisce sia valori semplici che oggetti)
    const isFieldEmpty = (value, fieldConfig) => {
        if (value === null || value === undefined || value === '') return true;

        switch (fieldConfig.input_type) {
            case 'option':
                // { value: '', label: '' } è considerato vuoto
                return typeof value === 'object' ? !value.value : !value;
            case 'externalEntity':
                // null o { id: '' } è considerato vuoto
                return typeof value === 'object' ? !value?.id : !value;
            default:
                return !value;
        }
    };

    const handleSave = async () => {
        // Validazione campi required
        for (const col of editableColumns) {
            // Salta validazione password in edit (non è mostrata)
            if (col === 'password' && editingUser.id) continue;

            const fieldConfig = props.columns[col];
            if (fieldConfig?.validation?.required && isFieldEmpty(editingUser[col], fieldConfig)) {
                toast.error(`${LanguageProvider.getLabel(ENTITY, col)} è obbligatorio`);
                return;
            }
        }

        // Password obbligatoria per nuovo utente
        if (!editingUser.id && editableColumns.includes('password') && !editingUser.password) {
            toast.error('La password è obbligatoria per i nuovi utenti');
            return;
        }

        setSaving(true);
        try {
            const data = {};

            editableColumns.forEach(col => {
                const fieldConfig = props.columns[col];
                if (fieldConfig) {
                    // Salta password vuota in edit
                    if (col === 'password' && editingUser.id && !editingUser[col]) {
                        return;
                    }
                    data[col] = prepareFieldForSave(editingUser[col], fieldConfig);
                }
            });

            let response;
            if (editingUser.id) {
                // Usa la nuova API /users/edit per modificare utenti esistenti
                data.id = editingUser.id;
                response = await ApiManager.sendAuthenticatedRequest('/users/edit', data);
            } else {
                // Per nuovi utenti usa il DataManager standard
                response = await DataManager.insertItem(ENTITY, data);
            }

            if (response.success === 1) {
                toast.success(editingUser.id ? 'Utente aggiornato' : 'Utente creato');
                setShowEditModal(false);
                setEditingUser(null);
                loadUsers();
            } else {
                toast.error(response.message || 'Errore nel salvataggio');
            }
        } catch (error) {
            console.error('Error saving user:', error);
            toast.error('Errore nel salvataggio');
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = (user) => {
        setPasswordUser(user);
        setNewPassword('');
        setConfirmPassword('');
        setShowPasswordModal(true);
    };

    const handleSavePassword = async () => {
        if (!newPassword) {
            toast.error('Inserisci la nuova password');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Le password non coincidono');
            return;
        }
        if (newPassword.length < 6) {
            toast.error('La password deve essere di almeno 6 caratteri');
            return;
        }

        setSavingPassword(true);
        try {
            const response = await DataManager.updateItem(ENTITY, {
                password: newPassword
            }, passwordUser.id);

            if (response.success === 1) {
                toast.success('Password aggiornata');
                setShowPasswordModal(false);
                setPasswordUser(null);
            } else {
                toast.error(response.message || 'Errore nel salvataggio');
            }
        } catch (error) {
            console.error('Error saving password:', error);
            toast.error('Errore nel salvataggio');
        } finally {
            setSavingPassword(false);
        }
    };

    const handleDeleteClick = (user) => {
        setDeletingUser(user);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!deletingUser) return;

        setDeleting(true);
        try {
            const response = await DataManager.deleteItem(ENTITY, deletingUser.id);
            if (response.success === 1) {
                toast.success('Utente eliminato');
                setShowDeleteModal(false);
                setDeletingUser(null);
                loadUsers();
            } else {
                toast.error(response.message || 'Errore nell\'eliminazione');
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error('Errore nell\'eliminazione');
        } finally {
            setDeleting(false);
        }
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage < maxVisible - 1) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        items.push(
            <Pagination.First key="first" onClick={() => setPage(1)} disabled={page === 1} />,
            <Pagination.Prev key="prev" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} />
        );

        if (startPage > 1) {
            items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        }

        for (let i = startPage; i <= endPage; i++) {
            items.push(
                <Pagination.Item key={i} active={i === page} onClick={() => setPage(i)}>
                    {i}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        }

        items.push(
            <Pagination.Next key="next" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} />,
            <Pagination.Last key="last" onClick={() => setPage(totalPages)} disabled={page === totalPages} />
        );

        return <Pagination className="mb-0">{items}</Pagination>;
    };

    if (!canView) {
        return (
            <Container fluid className="users-page py-4">
                <div className="text-center py-5">
                    <FontAwesomeIcon icon="lock" size="3x" className="text-muted mb-3" />
                    <p className="text-muted">Non hai i permessi per visualizzare questa pagina</p>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="users-page py-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="mb-0">
                    {LanguageProvider.get('entity.' + ENTITY)}
                </h3>
                {canCreate && (
                    <Button variant="primary" onClick={handleAdd}>
                        <FontAwesomeIcon icon="plus" className="me-2" />
                        Nuovo Utente
                    </Button>
                )}
            </div>

            {activeTab !== 'deleted' && (
                <InputGroup className="mb-3">
                    <InputGroup.Text>
                        <FontAwesomeIcon icon="search" />
                    </InputGroup.Text>
                    <Form.Control
                        type="text"
                        placeholder="Cerca per nome, cognome, username o email..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                    {searchQuery && (
                        <Button variant="outline-secondary" onClick={() => handleSearch('')}>
                            <FontAwesomeIcon icon="times" />
                        </Button>
                    )}
                </InputGroup>
            )}

            <Card>
                <Card.Body>
                    <Nav variant="tabs" className="mb-3" activeKey={activeTab} onSelect={handleTabChange}>
                        <Nav.Item>
                            <Nav.Link eventKey="active" className='small'>
                                <FontAwesomeIcon icon="users" className="me-2" />
                                Utenti Attivi
                            </Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="disabled" className='small'>
                                <FontAwesomeIcon icon="user-slash" className="me-2" />
                                Utenti Disabilitati
                            </Nav.Link>
                        </Nav.Item>
                        {canDelete && (
                            <Nav.Item>
                                <Nav.Link eventKey="deleted" className='small'>
                                    <FontAwesomeIcon icon="trash" className="me-2" />
                                    Utenti Eliminati
                                </Nav.Link>
                            </Nav.Item>
                        )}
                    </Nav>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <>
                            <Table responsive hover size="sm" className="mb-0 small">
                                <thead>
                                    <tr>
                                        <th style={{ width: '60px' }}></th>
                                        {visibleColumns.map((col) => (
                                            <th key={col}>{LanguageProvider.getLabel(ENTITY, col)}</th>
                                        ))}
                                        {(canEdit || canDelete) && <th style={{ width: '150px' }}>{LanguageProvider.get('defaults.actions', 'Azioni')}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.length === 0 ? (
                                        <tr>
                                            <td colSpan={visibleColumns.length + 1 + (canEdit || canDelete ? 1 : 0)} className="text-center py-4 text-muted">
                                                {LanguageProvider.get('defaults.no_results', 'Nessun elemento trovato')}
                                            </td>
                                        </tr>
                                    ) : (
                                        users.map((user) => (
                                            <tr key={user.id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        {getAvatarImage(user) ? (
                                                            <Image
                                                                src={getAvatarImage(user)}
                                                                alt={user.name}
                                                                className={`users-page__avatar ${activeTab === 'disabled' ? 'opacity-50' : ''}`}
                                                                width={40}
                                                                height={40}
                                                            />
                                                        ) : (
                                                            <div className={`users-page__avatar-placeholder ${activeTab === 'disabled' ? 'opacity-50' : ''}`}>
                                                                <FontAwesomeIcon icon="user" />
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                {visibleColumns.map((col) => (
                                                    <td key={col}>
                                                        <FieldPreview item={user} field={col} props={props} entity={ENTITY} />
                                                    </td>
                                                ))}
                                                {(canEdit || canDelete) && (
                                                    <td>
                                                        {activeTab === 'deleted' ? (
                                                            // Tab eliminati: solo ripristina
                                                            <Button
                                                                variant="outline-success"
                                                                size="sm"
                                                                onClick={() => handleRestoreClick(user)}
                                                                title="Ripristina utente"
                                                                className='m-1'
                                                            >
                                                                <FontAwesomeIcon icon="undo" />
                                                            </Button>
                                                        ) : (
                                                            // Tab attivi/disabilitati: tutte le azioni
                                                            <>
                                                                {canEdit && (
                                                                    <>
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            className="m-1"
                                                                            onClick={() => handleEdit(user)}
                                                                            title="Modifica"
                                                                        >
                                                                            <FontAwesomeIcon icon="edit" />
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline-warning"
                                                                            size="sm"
                                                                            className="m-1"
                                                                            onClick={() => handlePasswordChange(user)}
                                                                            title="Cambia password"
                                                                        >
                                                                            <FontAwesomeIcon icon="key" />
                                                                        </Button>
                                                                        {activeTab === 'active' ? (
                                                                            <Button
                                                                                variant="outline-secondary"
                                                                                size="sm"
                                                                                className="m-1"
                                                                                onClick={() => handleDisableClick(user)}
                                                                                title="Disabilita utente"
                                                                            >
                                                                                <FontAwesomeIcon icon="ban" />
                                                                            </Button>
                                                                        ) : (
                                                                            <Button
                                                                                variant="outline-success"
                                                                                size="sm"
                                                                                className="m-1"
                                                                                onClick={() => handleEnableClick(user)}
                                                                                title="Riabilita utente"
                                                                            >
                                                                                <FontAwesomeIcon icon="check" />
                                                                            </Button>
                                                                        )}
                                                                    </>
                                                                )}
                                                                {canDelete && (
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteClick(user)}
                                                                        title="Elimina"
                                                                        className='m-1'
                                                                    >
                                                                        <FontAwesomeIcon icon="trash" />
                                                                    </Button>
                                                                )}
                                                            </>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>
                            <div className="d-flex justify-content-between align-items-center p-3 border-top">
                                <small className="text-muted">
                                    {totalItems} {LanguageProvider.get('defaults.elements', 'elementi')}
                                </small>
                                {renderPagination()}
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* Modal Modifica Utente */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingUser?.id ? 'Modifica Utente' : 'Nuovo Utente'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {editingUser && props && (
                        <Form>
                            <Row>
                                {editableColumns.map((col) => {
                                    const fieldConfig = props.columns[col];
                                    if (!fieldConfig) return null;

                                    // Password: mostra solo in creazione
                                    if (col === 'password' && editingUser.id) return null;

                                    // Avatar: rendering custom
                                    if (col === 'avatar') return null;

                                    const inputType = fieldConfig.input_type;

                                    // Determina la larghezza della colonna in base al tipo
                                    let colClass = 'col-md-6 mb-3';
                                    if (inputType === 'text' || inputType === 'richText' || inputType === 'deepExternalEntity') {
                                        colClass = 'col-12 mb-3';
                                    }

                                    return (
                                        <Col key={col} className={colClass}>
                                            <Field
                                                type={inputType}
                                                fieldKey={col}
                                                value={editingUser[col]}
                                                entity={ENTITY}
                                                view={{ properties: props }}
                                                save={handleFieldChange}
                                                data={editingUser}
                                                noclass={true}
                                            />
                                        </Col>
                                    );
                                })}
                            </Row>
                            {/* Avatar custom */}
                            {editableColumns.includes('avatar') && (
                                <Row>
                                    <Col xs={12}>
                                        <Form.Group className="mb-3">
                                            <Form.Label className="small">{LanguageProvider.getLabel(ENTITY, 'avatar')}</Form.Label>
                                            <div className="d-flex flex-wrap gap-2">
                                                {avatars.map((avatarImg, i) => {
                                                    // Avatar può essere un numero o un oggetto { value: X }
                                                    const currentAvatar = editingUser.avatar?.value !== undefined
                                                        ? editingUser.avatar.value
                                                        : editingUser.avatar;
                                                    return (
                                                        <div
                                                            key={i}
                                                            className={`users-page__avatar-option ${currentAvatar == i ? 'selected' : ''}`}
                                                            onClick={() => setEditingUser({ ...editingUser, avatar: i })}
                                                        >
                                                            <Image src={avatarImg} width={50} height={50} />
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}
                        </Form>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="save" className="me-2" />
                                Salva
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Cambio Password */}
            <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        Cambia Password
                        {passwordUser && <small className="d-block text-muted">{passwordUser.username}</small>}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="small">Nuova Password</Form.Label>
                            <Form.Control
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Inserisci nuova password"
                            />
                            <Form.Text className="text-muted">
                                Minimo 6 caratteri
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="small">Conferma Password</Form.Label>
                            <Form.Control
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Conferma password"
                                isInvalid={confirmPassword && newPassword !== confirmPassword}
                            />
                            {confirmPassword && newPassword !== confirmPassword && (
                                <Form.Control.Feedback type="invalid">
                                    Le password non coincidono
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="warning" onClick={handleSavePassword} disabled={savingPassword}>
                        {savingPassword ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Salvataggio...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="key" className="me-2" />
                                Aggiorna Password
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Conferma Eliminazione */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Conferma Eliminazione</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {deletingUser && (
                        <p>
                            Sei sicuro di voler eliminare l'utente <strong>{deletingUser.name} {deletingUser.surname}</strong> ({deletingUser.username})?
                        </p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
                        {deleting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Eliminazione...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="trash" className="me-2" />
                                Elimina
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Conferma Disabilitazione */}
            <Modal show={showDisableModal} onHide={() => setShowDisableModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Disabilita Utente</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {disablingUser && (
                        <>
                            <p>
                                Sei sicuro di voler disabilitare l'utente <strong>{disablingUser.name} {disablingUser.surname}</strong> ({disablingUser.username})?
                            </p>
                            <p className="text-muted small">
                                L'utente non potrà più accedere al sistema e non potrà recuperare la password.
                            </p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDisableModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="dark" onClick={handleConfirmDisable} disabled={disabling}>
                        {disabling ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Disabilitazione...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="ban" className="me-2" />
                                Disabilita
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Conferma Riabilitazione */}
            <Modal show={showEnableModal} onHide={() => setShowEnableModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Riabilita Utente</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {enablingUser && (
                        <>
                            <p>
                                Sei sicuro di voler riabilitare l'utente <strong>{enablingUser.name} {enablingUser.surname}</strong> ({enablingUser.username})?
                            </p>
                            <p className="text-muted small">
                                L'utente potrà nuovamente accedere al sistema con le credenziali esistenti.
                            </p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEnableModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="success" onClick={handleConfirmEnable} disabled={enabling}>
                        {enabling ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Riabilitazione...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="check" className="me-2" />
                                Riabilita
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Conferma Ripristino */}
            <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Ripristina Utente</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {restoringUser && (
                        <>
                            <p>
                                Sei sicuro di voler ripristinare l'utente <strong>{restoringUser.name} {restoringUser.surname}</strong> ({restoringUser.username})?
                            </p>
                            <p className="text-muted small">
                                L'utente verrà ripristinato e sarà nuovamente visibile nella lista utenti attivi.
                            </p>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRestoreModal(false)}>
                        Annulla
                    </Button>
                    <Button variant="success" onClick={handleConfirmRestore} disabled={restoring}>
                        {restoring ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Ripristino...
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon="undo" className="me-2" />
                                Ripristina
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}
